import { Box, Grid, Typography, makeStyles } from '@material-ui/core';
import { DefaultCard, SubTitle1Text, colors } from 'design-system';
import React from 'react';

// Models
import EmissionDataFields from 'src/models/helperTypes/EmissionDataFields';

// Functions
import { identificationNumberString } from 'src/functions/identificationTypeString';
import parseCustomerSummaryData, {
  CustomerSummaryData,
  displayTextMap,
} from 'src/functions/parseCustomerSummaryData';

type Props = {
  customerType: 'N' | 'J';
  dataFields: EmissionDataFields;
  preventCollapse?: boolean;
  isCollective?: boolean;
};

const useStyles = makeStyles({
  fieldLabel: {
    color: colors.abbey,
    fontSize: 14,
  },
  logo: { marginRight: 11 },
});

/**
 * Some of the text labels are rendered dynamically. This will help us futureproof
 * upcoming dynamic labels. For now it is only an if statement, as we grow more
 * scenarios, a switch statement would be preferred.
 */
function parseDynamicDisplayText(key: string, customerType: string): string {
  if (key === 'idNumber') {
    return identificationNumberString(customerType);
  }
  return '';
}

/**
 * Renders each corresponding label and value for the customer's summary data
 * @param data Either natural person or legal entity customer data
 * @param classes Styles
 */
function renderDisplayTexts(
  data: CustomerSummaryData,
  classes: ReturnType<typeof useStyles>,
): React.ReactNode[] {
  type DataKey = keyof typeof data;
  return Object.keys(data).map((key) => {
    // text to be displayed as label
    let displayText = '';

    // try to find label in the displayTextMap
    if (key in displayTextMap) {
      displayText = displayTextMap[key];
    } else {
      // not found in predefined displayTextMap, so label is dynamic
      displayText = parseDynamicDisplayText(key, data.customerType);
    }
    return key === 'customerType' ? (
      <div key={`${key}`} /> // skip customerType
    ) : (
      <Grid key={`${key}`} item xs={12} md={6}>
        <Typography variant="caption" className={classes.fieldLabel}>
          {displayText}
        </Typography>
        <Typography variant="body2">{data[key as DataKey]}</Typography>
      </Grid>
    );
  });
}

/**
 * Displays customer's summary data
 */
const CustomerDataCard = ({
  customerType,
  dataFields,
  preventCollapse = false,
  isCollective,
}: Props): JSX.Element => {
  const classes = useStyles();
  const parsedData = parseCustomerSummaryData(
    customerType,
    dataFields,
    isCollective,
  );

  return (
    <DefaultCard
      data-testid="customer-data-card"
      title={
        <Grid item>
          <Box display="flex">
            <img
              src="svg/profile_icon_color_blue.svg"
              alt="profile_icon_color_blue_icon"
              className={classes.logo}
              data-testid="icon-image"
            />
            <SubTitle1Text>Datos del contratante</SubTitle1Text>
          </Box>
        </Grid>
      }
      collapsible={!preventCollapse}
      startCollapsed
    >
      <Grid container spacing={2}>
        {renderDisplayTexts(parsedData, classes)}
      </Grid>
    </DefaultCard>
  );
};

export default CustomerDataCard;
